import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import SDNotification from '@/shared/components/SDComponentsNewDesign/SDNotification/SDNotification'
import { toIntlLocale } from '@/shared/tools/toIntlLocale'
import MeterInformationAccordion from '@/containers/SubscriptionFormPageNew/Components/shared/Meter/MeterInformationAccordion'
import { formatMaxReachedPower } from '@/containers/SubscriptionFormPageNew/Components/ElectricityMeter/formatMaxReachedPower'

const MeterCompleteInformation = ({ currentValue, oldOccupant, type }) => {
  const { t, i18n } = useTranslation()
  const enedisData = currentValue?.elec?.sentences ?? {}
  const enedisDataBasic = currentValue?.elec ?? null
  const panels = []
  const panelsBasic = []
  const filterErrors = errors => {
    const list = []
    for (const [key, value] of Object.entries(errors)) {
      list.push(<SDNotification type='warning' primaryText={value} key={'enedis_error' + key} />)
    }
    return list
  }
  const isNotBaseOrHpHc = meterOptionValue => {
    return meterOptionValue !== 'base' && meterOptionValue !== 'hphc'
  }
  const getErrors = errors => {
    if (!Array.isArray(errors) && typeof errors !== 'object') {
      return
    }
    if (typeof errors === 'object') {
      return filterErrors(Object.values(errors))
    }
    const uniqueErrors = errors.filter((x, i) => errors.indexOf(x) === i)
    return filterErrors(uniqueErrors)
  }
  // Consumption
  if (Object.entries(enedisData).length > 0 && type !== 'contract') {
    const text = []
    for (const [, textValue] of Object.entries(enedisData)) {
      text.push({
        text: '',
        value: textValue,
      })
    }

    panels.push({
      title: t('subscriptionForm:pdl_module.meter_complete_information.consumption_title'),
      text: text.length > 0 ? text : [t('subscriptionForm:pdl_module.meter_complete_information.no_info_available')],
    })
  }

  // Basic information
  if (
    enedisDataBasic?.technicalData?.status ||
    enedisDataBasic?.technicalData?.meterPower ||
    enedisDataBasic?.maxPower ||
    enedisDataBasic?.technicalData?.meterType ||
    enedisDataBasic?.technicalData?.matricule ||
    enedisDataBasic?.technicalData?.rawMeterOption ||
    enedisDataBasic?.technicalData?.provider ||
    enedisDataBasic?.technicalData?.serviceOpening ||
    enedisDataBasic?.technicalData?.dateLastModification?.formuleTarifaireAcheminement ||
    enedisDataBasic?.technicalData?.offPeakHours ||
    enedisDataBasic?.technicalData?.supplySituation?.raccordementSoutiragePower ||
    enedisDataBasic?.consumption?.yearlyExtrapolation ||
    oldOccupant
  ) {
    const state = enedisDataBasic?.technicalData?.status
    const power = enedisDataBasic?.technicalData?.meterPower
    const maxReachedPower = enedisDataBasic?.maxPower
    const type = enedisDataBasic?.technicalData?.meterType
    const matricule = enedisDataBasic?.technicalData?.matricule
    const option = enedisDataBasic?.technicalData?.rawMeterOption
    const originalRawMeterOption = enedisDataBasic?.technicalData?.tariff_option_raw_enedis
    const originalMeterOption = enedisDataBasic?.technicalData?.tariff_option_enedis
    const currentProvider = enedisDataBasic?.technicalData?.provider
    const serviceOpening = enedisDataBasic?.technicalData?.serviceOpening ?? ''
    const formuleTarifaireAcheminement =
      enedisDataBasic?.technicalData?.dateLastModification?.formuleTarifaireAcheminement
    const offPeakHours = enedisDataBasic?.technicalData?.offPeakHours
    const raccordementSoutiragePower = enedisDataBasic?.technicalData?.supplySituation?.raccordementSoutiragePower
    const amperage = enedisDataBasic?.technicalData?.circuitBreakerCaliber
    const historicalConsumption = enedisDataBasic?.consumption?.yearlyExtrapolation
    const text = []

    if (currentProvider) {
      text.push({
        text: t('subscriptionForm:pdl_module.meter_complete_information.basic_information.counter_currentProvider'),
        value: currentProvider,
      })
    }

    if (state) {
      text.push({
        text: t('subscriptionForm:pdl_module.meter_complete_information.basic_information.counter_state'),
        value: state,
      })
    }

    if (power) {
      text.push({
        text: t('subscriptionForm:pdl_module.meter_complete_information.basic_information.power_subscribed'),
        value: `${power} kVA`,
      })
    }

    const maxReachedPowerText = maxReachedPower
      ? formatMaxReachedPower(maxReachedPower, toIntlLocale(i18n.language))
      : null
    if (maxReachedPowerText) {
      text.push({
        text: t('subscriptionForm:pdl_module.meter_complete_information.basic_information.max_reached_power'),
        value: maxReachedPowerText,
      })
    }

    if (type) {
      text.push({
        text: t('subscriptionForm:pdl_module.meter_complete_information.basic_information.counter_type'),
        value: type,
      })
    }

    if (matricule) {
      text.push({
        text: t('subscriptionForm:pdl_module.meter_complete_information.basic_information.registration_number'),
        value: matricule,
      })
    }

    if (isNotBaseOrHpHc(originalMeterOption) && originalMeterOption && originalMeterOption !== originalRawMeterOption) {
      text.push({
        text: t('subscriptionForm:pdl_module.meter_complete_information.basic_information.tariff_option_enedis'),
        value: originalMeterOption,
      })
    }

    if (
      isNotBaseOrHpHc(originalRawMeterOption) &&
      originalRawMeterOption &&
      originalRawMeterOption !== originalMeterOption
    ) {
      text.push({
        text: t('subscriptionForm:pdl_module.meter_complete_information.basic_information.tariff_option_raw_enedis'),
        value: originalRawMeterOption,
      })
    }

    if (option) {
      text.push({
        text: t('subscriptionForm:pdl_module.meter_complete_information.basic_information.tariff_option'),
        value: option,
      })
    }

    if (serviceOpening) {
      if (serviceOpening === '0') {
        text.push({
          text: t('subscriptionForm:pdl_module.meter_complete_information.basic_information.service_opening'),
          value: t('subscriptionForm:pdl_module.meter_complete_information.basic_information.no_service_opening'),
        })
      }
      if (serviceOpening === '1') {
        text.push({
          text: t('subscriptionForm:pdl_module.meter_complete_information.basic_information.service_opening'),
          value: t('subscriptionForm:pdl_module.meter_complete_information.basic_information.non_communicating_linky'),
        })
      }
      if (serviceOpening === '2') {
        text.push({
          text: t('subscriptionForm:pdl_module.meter_complete_information.basic_information.service_opening'),
          value: t('subscriptionForm:pdl_module.meter_complete_information.basic_information.communicating_linky'),
        })
      }
    }

    if (formuleTarifaireAcheminement) {
      const lastDate = formuleTarifaireAcheminement.slice(0, 10).split('-').reverse().join('/')
      text.push({
        text: t('subscriptionForm:pdl_module.meter_complete_information.basic_information.last_modification'),
        value: lastDate,
      })
    }

    if (offPeakHours) {
      text.push({
        text: t('subscriptionForm:pdl_module.meter_complete_information.basic_information.off_peak_hours'),
        value: offPeakHours,
      })
    }

    if (raccordementSoutiragePower) {
      text.push({
        text: t(
          'subscriptionForm:pdl_module.meter_complete_information.basic_information.raccordement_soutirage_power',
        ),
        value: raccordementSoutiragePower,
      })
    }

    if (amperage) {
      text.push({
        text: t('subscriptionForm:pdl_module.meter_complete_information.basic_information.amperage'),
        value: amperage,
      })
    }

    if (historicalConsumption) {
      text.push({
        text: t('subscriptionForm:pdl_module.meter_complete_information.basic_information.historical_consumption'),
        value: `${historicalConsumption} kWh/an`,
      })
    } else {
      text.push({
        text: t('subscriptionForm:pdl_module.meter_complete_information.basic_information.historical_consumption'),
        value: t('subscriptionForm:pdl_module.meter_complete_information.no_info_available'),
      })
    }

    if (oldOccupant) {
      text.push({
        text: t('subscriptionForm:pdl_module.meter_complete_information.basic_information.old_occupant'),
        value: oldOccupant,
      })
    }

    panelsBasic.push({
      title: t('subscriptionForm:pdl_module.meter_complete_information.basic_information.title'),
      text: text.length > 0 ? text : [t('subscriptionForm:pdl_module.meter_complete_information.no_info_available')],
    })
  }

  // AddressSearch information
  if (enedisDataBasic?.technicalData?.address && type !== 'contract') {
    const address = enedisDataBasic?.technicalData?.address
    const text = []

    if (address.name) {
      const streetNumber = address.number ? address.number + ' ' : ''
      text.push({
        text: '',
        value: streetNumber + address.name,
      })
    }

    if (address.building?.length > 0) {
      text.push({
        text: t('subscriptionForm:pdl_module.meter_complete_information.address.building'),
        value: address.building,
      })
    }

    if (address.additionnal?.length > 0) {
      text.push({
        text: t('subscriptionForm:pdl_module.meter_complete_information.address.complement'),
        value: address.additionnal,
      })
    }

    if (address.city) {
      text.push({
        text: '',
        value: `${address.zipcode ?? ''}${address.postcode ?? ''} ${address.city}`,
      })
    }

    panelsBasic.push({
      title: t('subscriptionForm:pdl_module.meter_complete_information.address.address'),
      text: text.length > 0 ? text : [t('subscriptionForm:pdl_module.meter_complete_information.no_info_available')],
    })
  }

  if (
    enedisDataBasic?.technicalData &&
    Object.entries(enedisDataBasic?.technicalData).length > 0 &&
    !enedisDataBasic?.technicalData?.pdl
  ) {
    return (
      <>
        <SDNotification type='warning' primaryText={t('subscriptionForm:pdl_module.error_enedis')} />
        {enedisDataBasic?.warnings &&
          Object.entries(enedisDataBasic?.warnings).length > 0 &&
          getErrors(enedisDataBasic?.warnings)}
      </>
    )
  }

  return <MeterInformationAccordion mainPanels={panelsBasic} otherPanels={panels} />
}

MeterCompleteInformation.propTypes = {
  currentValue: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  oldOccupant: PropTypes.string,
}

export default MeterCompleteInformation
